import fetch from 'cross-fetch'
import {ApolloClient, InMemoryCache, createHttpLink} from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import {ErrorLink} from '@apollo/client/link/error'
import {from, mergeMap, throwError} from 'rxjs'

import typePolicies from './typePolicies'
import {getItem} from '../lib/authTools'
import {LOCKED_OPERATIONS, getLockedToken, sealLocked, touchLocked} from '../lib/lockedTools'
import {SEALED, UNAUTHORIZED, says} from '../lib/errorTools'
import {refreshSession} from '../lib/sessionTools'

const configuration = () => {
  let config = {uri: `${API_HOST}/graphql`}

  // In some test envrionment, javascript fetch object is unavailable. This makes sure that cross-fetch is able to handle such environment
  if (__TEST__)
    config = {...config, fetch}

  return config
}

const httpLink = createHttpLink(configuration())

const authLink = setContext(({operationName}, {headers}) => {
  const token = getItem('authToken')
  const lockedToken = getLockedToken()

  if (lockedToken && LOCKED_OPERATIONS.has(operationName)) touchLocked()

  return {
    headers: {
      ...headers,
      authorization: token || '',
      // Only while Locked is open in this tab. Sent with everything rather than picked out, since
      // whether a request touches Locked can depend on what it names.
      ...(lockedToken && {'x-locked-token': lockedToken})
    },
    // The session this request is going out on. Read back by the error link if it comes back
    // refused, to tell a request that lapsed from one that was merely in the air while the
    // session was renewed underneath it.
    sentWith: token
  }
})

// One retry is the ceiling, and it is Apollo that sets it: the error link is not re-entered for
// an operation it forwarded itself, so a retry that comes back refused goes straight to the caller.
const errorLink = new ErrorLink(({error, operation, forward}) => {
  // The API is the one that keeps Locked open, so its word that Locked has resealed seals it here.
  // The error still reaches the caller, which says so in its own way.
  if (says(error, SEALED)) sealLocked()

  if (!says(error, UNAUTHORIZED)) return

  return from(refreshSession(operation.getContext().sentWith)).pipe(
    // A renewal that failed has already cleared what it needed to. Letting the original error
    // through is what tells the caller so.
    mergeMap(renewed => (renewed ? forward(operation) : throwError(() => error)))
  )
})

const client = new ApolloClient({
  // The error link sits above the auth link so that a retry goes back through setContext and
  // picks up the token the renewal just wrote.
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache({typePolicies})
})

export default client
